import { pointer, select  } from 'd3-selection';
import { symbol, symbolTriangle } from 'd3-shape';
import { timeDay, timeHour, timeMinute } from 'd3-time';

import { server } from '../actions/fetch';
import aapsLogo from '../images/aaps.svg';
// import cyclingIcon from '../images/cycling.svg';
import noteIcon from '../images/note.svg';
// import runningIcon from '../images/running.svg';
// import yogaIcon from '../images/yoga.svg';
import { sortAscendingTime } from '../reducers/data';
import { idOrTimeKey, timeAccessor, timeSort } from '../utils/dataUtils';
import { parseDate } from '../utils/formats';
import { adjustLayout } from '../utils/layoutUtils';
import { /* fastTransition, */ getSlowTransition, remove } from '../utils/svgUtils';
import './Dashboard.css';

const infoRowClasses = ['carbs', 'bolus', 'basal', 'long', 'activity', 'notes'];
const infoBarHeight = 8;
const infoRowMarkerHeight = 4;
const infoRowTextHeight = 10;
const infoRowHeight = infoBarHeight + infoRowMarkerHeight + infoRowTextHeight;

const eventMarker = symbol().type(symbolTriangle).size(infoRowMarkerHeight);

const bolusEffectTime = 240; // in minutes
const longActingEffectTime = 24; // in hours
const mealEffectTime = 180; // in minutes

const formatTBR = d => (d.percent ? ` (${d.percent} %)` : '');

// Type can be 'sleep' for Oura, 'sleeping' for Google FIT
const isSleep = d => d.type && d.type.indexOf('sleep') === 0;

const isStepsOnly = d => d.steps && !d.activity;

// This is better for translations?
// const activityTextAccessor = d => d.type || d.exercise || d.activity;
// This is better for no translations
// const activityTextAccessor = d => d.exercise || d.type || d.activity || 'undefined activity';

export const getActivityAccessor = t => (d) => {
  if (d.exercise && t([d.exercise.toLowerCase(), ''], { ns: 'activities' })) {
    return d.exercise.toLowerCase();
  }
  if (d.type && t([d.type.toLowerCase(), ''], { ns: 'activities' })) {
    return d.type.toLowerCase();
  }
  if (d.activity) {
    return d.activity.toLowerCase();
  }
  return d.exercise?.toLowerCase()
  || d.type?.toLowerCase()
  || d.activity?.toLowerCase()
  || 'undefined activity';
};

export const getActivityTextAccessor = t => (d) => 
  t([getActivityAccessor(t)(d), 'undefined activity'], { ns: 'activities' });

const formatTimeRange = ({ d, formatTimeString, t }) => {
  const startTime = new Date(d.time);
  const endTime = new Date(d.endTime);
  const from = formatTimeString(startTime);
  const to = formatTimeString(endTime);
  return `${from}${t('common.rangeSeparator')}${to}`;
};

const getActivityTitleAccessor = ({ formatTimeString, t }) => (d) => {
  const startTime = new Date(d.time);
  const endTime = new Date(d.endTime);
  const from = formatTimeString(startTime);
  const to = formatTimeString(endTime);

  if (isSleep(d)) {
    const duration = timeMinute.count(startTime, endTime);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return t('daily.sleep', { from, to, duration, hours, minutes });
  }
  if (isStepsOnly(d)) {
    return `${
      formatTimeRange({ d, formatTimeString, t })
    }\n${
      t('daily.steps', { count: d.steps })
    }`;
  }
  return `${formatTimeRange({ d, formatTimeString, t })}\n${JSON.stringify(d, null, 4)}`;
};

const activityLabelFilter = d => ((d.activityType !== 'transport') && !isSleep(d) && (!d.steps));
const getActivityClass = (d) => {
  if (isSleep(d)) {
    return 'sleep';
  }
  if (d.activityType === 'transport') {
    return 'transport-activity';
  }
  return 'activity-effect';
};

const excludeUndefined = (d) => !!d;

const basalTextAccessor = (d) => {
  if (d.raw && d.raw.notes && d.raw.notes.some && d.raw.notes.some(n => n.indexOf('PGS') !== -1)) {
    return 'SG';
  }
  if (d.raw && d.raw.notes && d.raw.notes.some && d.raw.notes.some(n => n.indexOf('LGS') !== -1)) {
    return 'LOW';
  }
  return d.rate ? d.rate.toFixed(2) : '-';
}

export const getBasalTitleAccessor = ({ formatTimeString, t }) => d => `${
  formatTimeRange({ d, formatTimeString, t })
}\n${
  basalTextAccessor(d)
} ${
  t('insulin.iu/h')
} ${
  formatTBR(d)
}`;

function getBasalEntryClass(d, i, a) {
  let baseClass = 'basal entry';
  if (d.duration) {
    baseClass += ' tbr';
    // For quickly changing basals (APS), we want to just mark the peaks...
    if ((i > 1) && (i < (a.length - 1))) {
      // a potential peak value
      // peak value is one where neighbouring values on both side are
      // either lower (high peak) or higher (valley bottom) than the current value
      // Note that an entry (especially the last one) may already exist.
      // In this case the slot in the received entry array is undefined!
      // (and in this case we won't set the turning point...)
      const prev = a[i - 1];
      const prevRate = prev && select(prev).datum().rate;
      const next = a[i + 1];
      const nextRate = next && select(next).datum().rate;
      if ((d.rate < prevRate) && (d.rate < nextRate)) {
        baseClass += ' low turningpoint';
      }
      if ((d.rate > prevRate) && (d.rate > nextRate)) {
        baseClass += ' high turningpoint';
      }
    }
  }
  return baseClass;
}

function getBasalRateClass(d) {
  let baseClass = 'basal-rate';
  if (d.duration) {
    baseClass += ' tbr';
    if (!d.rate) {
      baseClass += ' suspended';
    } else if (d.percent) {
      const value = Number.isNaN(d.percent) ? parseFloat(d.percent, 10) : d.percent;
      baseClass += value < 100 ? ' decreased' : ' increased';
    } else {
      // This could be higher for one profile step, lower for the next...
      baseClass += ' absolute';
    }
  }
  return baseClass;
}

const bolusTextAccessor = d => d.amount?.toFixed(1);

export const getBolusTitleAccessor = ({ formatTimeString, t }) => d => `${
  formatTimeString(new Date(d.time))
} ${
  bolusTextAccessor(d)
} ${
  t('insulin.iu')
}`;

const carbTextAccessor = d => d.carbohydrate;

export const getCarbTitleAccessor = ({ formatTimeString, t }) => d => `${
  formatTimeString(new Date(d.time))
} ${
  carbTextAccessor(d)
} g`;


export const photoThumbnailAccessor = d => d.thumbnailUrl.indexOf('http') === 0
? d.thumbnailUrl
: `${server}${d.thumbnailUrl}`;

function scaleUp(event, d) {
  const image = this;
  const imageCoords = image.getBoundingClientRect();
  const inspector = select('#inspector');
  const svg = image.ownerSVGElement;
  const svgCoords = svg.getBoundingClientRect();

  function showNewImage() {
    inspector.select('*').remove();

    let contents;
    if (d && d.thumbnailUrl) {
      contents = inspector.append('img')
        .attr('src', photoThumbnailAccessor(d));
    } else if (d && d.note) {
      contents = inspector.append('div')
        .attr('class', 'note')
        .datum(d.note)
        .text(d.note);
    } else {
      return;
    }
    
    inspector
      .style('top', `${imageCoords.top + window.pageYOffset}px`)
      .style('left', `${imageCoords.left}px`)
      .style('right', 'inherit')
      .style('width', `${imageCoords.width}px`)
      .style('height', `${imageCoords.height}px`)
      .style('background', 'none')
      .attr('open', '')
      .call(i => {
        function scaleMeUp() {
          const coords = contents.node().getBoundingClientRect();
          if (!coords.width || !coords.height) {
            window.requestAnimationFrame(scaleMeUp);
            return;
          }
          const aspectRatio = coords.width / coords.height;
          const targetHeight = 480; // TODO: scale this to the glucose SVG size, and also position accordingly?
          const targetWidth = d.thumbnailUrl ? (targetHeight * aspectRatio) : 480;
          const left = Math.max(
            svgCoords.left,
            Math.min(
              svgCoords.left + svgCoords.width - targetWidth,
              coords.left + (coords.width / 2) - (targetWidth / 2)
          ));
          select(image).classed('scaled', true);
          i.transition()
            .style('top', `${imageCoords.top + window.pageYOffset - (infoRowHeight * 4)}px`)
            .style('left', `${left}px`)
            .style('width', `${targetWidth}px`);
        }
        window.requestAnimationFrame(scaleMeUp);
      });

      select(document).on('click.hideDialog', function hideDialog() {
        inspector.transition()
          .style('top', `${imageCoords.top + window.pageYOffset}px`)
          .style('left', `${imageCoords.left}px`)
          .style('width', `${imageCoords.width}px`)
          .style('height', `${imageCoords.height}px`)
          .on('end', function finalize() {
            select(document).on('click.hideDialog', null);
            select(image).classed('scaled', false);
            inspector
              .attr('open', null)
              .style('bottom', null)
              .style('left', null)
              .style('right', null)
              .style('width', null)
              .style('height', null)
              .style('background', null);
          });
      });
    
  }

  if (inspector.attr('open') !== null) {
    const scaledImage = select(svg).select('image.scaled');
    const coords = scaledImage.node().getBoundingClientRect();

    function finalize() {
      scaledImage.classed('scaled', false);
      select(document).on('click.hideDialog', null);
      inspector
        .attr('open', null)
        .style('bottom', null)
        .style('left', null)
        .style('right', null)
        .style('width', null)
        .style('height', null)
        .style('background', null);
      showNewImage();
    }

    inspector.transition()
      .style('top', `${coords.top + window.pageYOffset}px`)
      .style('left', `${coords.left}px`)
      .style('width', `${coords.width}px`)
      .style('height', `${coords.height}px`)
      .on('interrupt', finalize)
      .on('end', finalize);
  } else {
    showNewImage();
  }
};

function renderNote(note) {
  const g = select(this);
  if (note.thumbnailUrl) {
    g.append('image')
      .attr('transform', `translate(${infoBarHeight * -0.75},${infoRowMarkerHeight})`)
      .attr('x', infoBarHeight * -1.5)
      .attr('width', infoBarHeight * 4.5)
      .attr('height', infoBarHeight * 3)
      .attr('preserveAspectRatio', 'xMidYMax')
      .attr('xlink:href', photoThumbnailAccessor)
      .on('click.showDialog', scaleUp);
  } else if (note.note || note.type) {
      g.append('image')
      .attr('transform', `translate(${infoBarHeight * -0.75},${infoRowMarkerHeight})`)
      .attr('width', infoBarHeight * 4.5)
      .attr('height', infoBarHeight * 3)
      .attr('preserveAspectRatio', 'xMidYMin')
      .attr('xlink:href', ((note.note && note.note.indexOf('AndroidAPS ') === 0) || (note.type && note.type === 'apsOffline')) ? aapsLogo : noteIcon)
      .on('click.showDialog', scaleUp);
  }
}


function getInfoRowRenderers({ basal, bolus, carbs, exercise, formatTimeString, locale, long, notes, sleep, steps, timeScale, t }) {
  // The scale may be different when DST changes...

  function getActivityWidth(d) {
    return timeScale({ time: d.endTime || timeMinute.offset(new Date(d.time), d.duration).toISOString() }) - timeScale(d);
  };

  function getBolusEffectWidth(d) {
    const dTime = timeAccessor(d);
    return timeScale({ time: timeMinute.offset(dTime, bolusEffectTime).toISOString() }) - timeScale(d);
  }

  function getLongActingWidth(d) {
    const dTime = timeAccessor(d);
    return timeScale({ time: timeHour.offset(dTime, longActingEffectTime).toISOString() }) - timeScale(d);
  }

  function getMealEffectWidth(d) {
    const dTime = timeAccessor(d);
    return timeScale({ time: timeMinute.offset(dTime, mealEffectTime).toISOString() }) - timeScale(d);
  }

  const maxOverlappingCarbs = 150; // TODO: calculate
  const getMealEffectFillOpacity = d => d.carbohydrate / maxOverlappingCarbs;

  const maxOverlappingBoluses = 15;
  const getBolusEffectFillOpacity = d => d.amount / maxOverlappingBoluses;

  const carbTitleAccessor = getCarbTitleAccessor({ formatTimeString, t});

  const bolusTitleAccessor = getBolusTitleAccessor({ formatTimeString, t});

  const basalTitleAccessor = getBasalTitleAccessor({ formatTimeString, t });
  const idKeyWithLocale = d => `${idOrTimeKey(d)}(${locale})`;

  const activityTextAccessor = getActivityTextAccessor(t);
  const activityTitleAccessor = getActivityTitleAccessor({ formatTimeString, t });

  const noteTitleAccessor = d => formatTimeString(new Date(d.time));

  return {
    carbs: function renderCarbs(selection) {
      const carbEntry = selection.selectAll('g.carb.entry').data(carbs, idKeyWithLocale);
      // TODO: after transition? carbEntry.exit().remove();
      const carbEntryEnter = carbEntry.enter().filter(d => !!d.carbohydrate).append('g')
        .attr('class', 'carb entry')
        .attr('transform', d => `translate(${timeScale(d)},0)`);
      carbEntryEnter.append('title');
      carbEntryEnter.append('g')
        .attr('class', 'adjustLayout')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', infoBarHeight + (infoRowMarkerHeight / 2) + (infoRowTextHeight * 0.1))
        .attr('dy', '1em');
      carbEntryEnter.append('path')
        .attr('class', 'event marker')
        .attr('transform', `translate(0,${infoBarHeight + (infoRowMarkerHeight / 2)})`)
        .attr('d', eventMarker);
      carbEntryEnter.append('rect')
        .attr('class', 'meal-effect')
        .attr('width', getMealEffectWidth)
        .attr('height', infoBarHeight)
        .style('fill-opacity', getMealEffectFillOpacity);
      const merged = carbEntryEnter.merge(carbEntry);
      merged
        .selectAll('text')
        .text(carbTextAccessor);
      merged
        .selectAll('title')
        .text(carbTitleAccessor);
    },
    bolus: function renderBoluses(selection) {
      const bolusEntry = selection.selectAll('g.bolus.entry').data(bolus, idKeyWithLocale);
      // TODO: after transition? bolusEntry.exit().remove();
      const bolusEntryEnter = bolusEntry.enter().append('g')
        .attr('class', 'bolus entry')
        .attr('transform', d => `translate(${timeScale(d)},0)`);
      bolusEntryEnter.append('title');
      bolusEntryEnter.append('g')
        .attr('class', 'adjustLayout')
      .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', infoBarHeight + (infoRowMarkerHeight / 2) + (infoRowTextHeight * 0.1))
        .attr('dy', '1em');
      bolusEntryEnter.append('path')
        .attr('class', 'event marker')
        .attr('transform', `translate(0,${infoBarHeight + (infoRowMarkerHeight / 2)})`)
        .attr('d', eventMarker);
      bolusEntryEnter.append('rect')
        .attr('class', 'bolus-effect')
        .attr('width', getBolusEffectWidth)
        .attr('height', infoBarHeight)
        .style('fill-opacity', getBolusEffectFillOpacity);
      const merged = bolusEntryEnter.merge(bolusEntry);
      merged
        .selectAll('text')
        .text(bolusTextAccessor);
      merged
        .selectAll('title')
        .text(bolusTitleAccessor);
    },
    basal: function renderBasals(selection) {
      const basalEntry = selection.selectAll('g.basal.entry').data(basal, idKeyWithLocale);
      // TODO: after transition? basalEntry.exit().remove();
      basalEntry.exit().remove();
      const basalEntryEnter = basalEntry.enter().append('g')
        .attr('class', getBasalEntryClass)
        .attr('transform', d => `translate(${timeScale(d)},0)`);
      basalEntryEnter.append('title');
      basalEntryEnter.append('g')
      .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', infoBarHeight + (infoRowMarkerHeight / 2) + (infoRowTextHeight * 0.1))
        .attr('dy', '1em');
      basalEntryEnter.append('path')
        .attr('class', 'event marker')
        .attr('transform', `translate(0,${infoBarHeight + (infoRowMarkerHeight / 2)})`)
        .attr('d', eventMarker);
      basalEntryEnter.append('rect')
        .attr('class', getBasalRateClass)
        .attr('width', d => (timeScale({ time: d.endTime }) - timeScale(d)))
        .attr('height', infoBarHeight);
      const merged = basalEntryEnter.merge(basalEntry);
      merged
        .selectAll('text')
        .text(basalTextAccessor);
      merged
        .selectAll('title')
        .text(basalTitleAccessor);
    },
    long: function renderLong(selection) {
      const longEntry = selection.selectAll('g.longActing.entry').data(long, idKeyWithLocale);
      longEntry.exit().remove();
      const longEntryEnter = longEntry.enter().append('g')
        .attr('class', 'longActing entry')
        .attr('transform', d => `translate(${timeScale(d)},0)`);

      longEntryEnter.append('g')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', infoBarHeight + (infoRowMarkerHeight / 2) + (infoRowTextHeight * 0.1))
        .attr('dy', '1em')
        .text(bolusTextAccessor);
      longEntryEnter.append('path')
        .attr('class', 'event marker')
        .attr('transform', `translate(0,${infoBarHeight + (infoRowMarkerHeight / 2)})`)
        .attr('d', eventMarker);
      longEntryEnter.append('rect')
        .attr('class', 'long-acting')
        .attr('width', getLongActingWidth)
        .attr('height', infoBarHeight)
        .append('title')
        .text(bolusTitleAccessor);
      longEntryEnter.merge(longEntry)
        .attr('transform', d => `translate(${timeScale(d)},0)`);
    },
    activity: function renderActivities(selection) {
      // console.log('Rendering activities', { exercise, sleep, steps });
      const allActivities = sortAscendingTime([...exercise, ...sleep, ...steps]);
      const activityEntry = selection.selectAll('g.activity.entry')
      .data(allActivities, idKeyWithLocale);
      // TODO: after transition? activityEntry.exit().remove();
      const activityEntryEnter = activityEntry.enter().append('g')
        .attr('class', 'activity entry');
      activityEntryEnter.append('title');
      const labeledActivities = activityEntryEnter.filter(activityLabelFilter);
      labeledActivities.append('g')
        .attr('class', 'adjustLayout')
      .append('text')
        .attr('text-anchor', 'start')
        .attr('y', infoBarHeight + (infoRowMarkerHeight / 2) + (infoRowTextHeight * 0.1))
        .attr('dy', '1em');
      labeledActivities.append('path')
        .attr('class', 'event marker')
        .attr('transform', `translate(0,${infoBarHeight + (infoRowMarkerHeight / 2)})`)
        .attr('d', eventMarker);
      activityEntryEnter.append('rect')
        .attr('class', getActivityClass)
        .attr('width', getActivityWidth)
        .attr('height', infoBarHeight)
        .style('fill-opacity', d => d.steps ? Math.min(d.steps / 500, 1) : null);
      const merged = activityEntryEnter.merge(activityEntry)
        .attr('transform', d => `translate(${timeScale(d)},0)`);
      merged
        .selectAll('text')
        .text(activityTextAccessor);
      merged
        .selectAll('title')
        .text(activityTitleAccessor);
    },
    notes: function renderNotes(selection) {
      // Filter duplicates, AAPS creates these...
      const filteredNotes = sortAscendingTime(Object.values(notes.reduce((o, v) => {
        // Don't include OpenAPS stuff for now...
        /*
        if ((!v.note || (v.note.indexOf('AndroidAPS ') !== 0)) && (!v.type || (v.type !== 'apsOffline'))) {
          o[v.id || v.time] = v;
        }
        */
        // Don't include any notes, just images!
        if (v.thumbnailUrl) {
          o[v.id || v.time] = v;
        }
        return o;
      }, {})));

      const noteEntry = selection.selectAll('g.note.entry').data(filteredNotes, idKeyWithLocale);
      // TODO: after transition? nodeEntry.exit().remove();
      const noteEntryEnter = noteEntry.enter().append('g')
        .attr('class', 'note entry');
      noteEntryEnter.append('title');
      const entryG = noteEntryEnter.append('g')
        .attr('class', 'adjustLayout');
      entryG.each(renderNote);
      noteEntryEnter.append('path')
        .attr('class', 'note event marker')
        .attr('d', eventMarker);
      const merged = noteEntryEnter.merge(noteEntry);
      merged.attr('transform', d => `translate(${timeScale(d)},${infoRowMarkerHeight})`)
      .selectAll('title')
        .text(noteTitleAccessor);
    },
  };
}

function render(selection) {
  const {
    data,
    day,
    formatTimeString,
    locale,
    timeScale,
    t,
  } = selection.datum();

  const {
    basal = [],
    bolus = [],
    carbs = [],
    events = [],
    exercise = [],
    longActing = [],
    notes = [],
    photos = [],
    sleep = [],
    steps = [],
  } = data;

  const date = parseDate(day);
  const timeObject = { time: date.toISOString() };
  const translateX = -timeScale(timeObject);
  const slowTransition = getSlowTransition();

  const svg = select(selection.node().ownerSVGElement);
  const defs = svg.select('defs');

  const mealGradient = defs.selectAll('linearGradient.meal-effect').data([1]);
  const mealGradientEnter = mealGradient.enter().append('linearGradient')
    .attr('id', 'mealGradient')
    .attr('class', 'meal-effect');
  mealGradientEnter.append('stop')
    .attr('offset', '0%')
    .attr('id', 'mealEffectStart');
  mealGradientEnter.append('stop')
    .attr('offset', '20%')
    .attr('id', 'mealEffectPeak');
  mealGradientEnter.append('stop')
    .attr('offset', '100%')
    .attr('id', 'mealEffectEnd');

  const bolusGradient = defs.selectAll('linearGradient.bolus-effect').data([1]);
  const bolusGradientEnter = bolusGradient.enter().append('linearGradient')
    .attr('id', 'bolusGradient')
    .attr('class', 'bolus-effect');
  bolusGradientEnter.append('stop')
    .attr('offset', '0%')
    .attr('id', 'bolusEffectStart');
  bolusGradientEnter.append('stop')
    .attr('offset', '15%')
    .attr('id', 'bolusEffectPeak');
  bolusGradientEnter.append('stop')
    .attr('offset', '100%')
    .attr('id', 'bolusEffectEnd');

  const graphsG = selection
    .selectAll('g.graphs')
    .data([1], Object)
    .join('g')
    .attr('class', 'graphs');

  const infoRowsG = graphsG.selectAll('g.infoRows');

  // Remove pending exiting transitions
  infoRowsG.filter(function (d) {
    return select(this).classed('exiting');
  }).remove();

  // Set labels according to data present
  const labelsG = svg.select('#InfoRowLabels');


  // The insulin labels may change dynamically:
  // - long acting, short acting, when long acting insulin is present
  // - basal, bolus, when no long acting insulin is present
  const presentInfoRows = infoRowClasses.reduce((o, c) => {
    if (c === 'long' && !longActing.length) {
      if (!basal.length) {
        o[c] = t(`daily.main.infoRow.${c}`);
      }
      return o;
    }
    if (c === 'basal' && !basal.length && longActing.length) {
      return o;
    }
    if (c === 'bolus' && !basal.length && longActing.length) {
      o[c] = t(`daily.main.infoRow.short`);
      return o;
    }
    o[c] = t(`daily.main.infoRow.${c}`);
    return o;
  }, {});

  const infoRows = Object.keys(presentInfoRows);

  const labels = labelsG.selectAll('.infoRow.label').data(infoRows, d => `${d}${presentInfoRows[d]}`);
  labels.exit().remove();

  const labelsEnter = labels.enter().append('text')
    .attr('class', d => `infoRow ${d} label`)
    .attr('x', -10)
    .text(d => presentInfoRows[d]);

  labels.merge(labelsEnter)
    .attr('y', (d, i) => (infoRowHeight * i) + infoBarHeight);

  let refTranslate;
  infoRowsG.each((d) => {
    if (d.timeScale) {
      refTranslate = d.timeScale(timeObject) - timeScale(timeObject);
    }
  });
  const plusOneDayTranslate = isNaN(refTranslate)
    ? translateX // refTranslate is not defined, we're starting from a clean sheet, no transitions!
    : (translateX + timeScale({ time: timeDay.offset(date, 1) }));

  const allNotes = [
    ...notes.filter(excludeUndefined),
    ...events.filter(excludeUndefined),
    ...photos.filter(excludeUndefined),
  ].sort(timeSort);

  const renderers = getInfoRowRenderers({
    basal: basal.filter(excludeUndefined),
    bolus: bolus.filter(excludeUndefined),
    carbs: carbs.filter(excludeUndefined),
    exercise: exercise.filter(excludeUndefined).filter(a => !(a.duration < 1)),
    formatTimeString,
    locale,
    long: longActing,
    notes: allNotes,
    sleep: sleep.filter(excludeUndefined),
    steps: steps.filter(excludeUndefined),
    timeScale,
    t,
  });

  infoRowsG
    .data([{ timeScale, date }], d => `${d.timeScale(timeObject)}(${locale})`)
    .join(
      enter => enter.append('g')
        .attr('class', 'infoRows')
        .style('shape-rendering', 'auto')
        .attr('transform', `translate(${(translateX >= refTranslate ? refTranslate : plusOneDayTranslate) || 0},0)`)
        .call(enter => {
          if (!enter.empty()) {
            enter.selectAll('g.infoRow')
              .data(infoRows)
              .enter()
              .append('g')
              .attr('class', d => `${d} infoRow layoutContainer`)
              .attr('transform', (d, i) => `translate(0,${i * infoRowHeight})`)
              .each(function (row) {
                const selectedRow = select(this);
                renderers[row](selectedRow);
                adjustLayout(selectedRow);
              });
            enter.classed('entering', true)
              .transition(slowTransition)
              .attr('transform', `translate(${translateX},0)`)
              .on('end', function (d) {
                select(this).classed('entering', false);
              });
          }
        }),
      update => update.call(update => {
        if (!update.empty()) {
          update.call(update => {
            update
              .attr('class', d => `infoRows at${d.timeScale(timeObject)}`)
              .classed('transitioning', true)
              .transition(slowTransition)
              .attr('transform', `translate(${translateX},0)`)
              .on('end', function (d) {
                select(this).classed('transitioning', false);
          });

          const rows = update.selectAll('g.infoRow')
            .data(infoRows, d => `${d}${presentInfoRows[d]}`);
          rows.transition(slowTransition)
            .attr('transform', (d, i) => `translate(0,${i * infoRowHeight})`)
            .each(function (row) {
              const selectedRow = select(this);
              renderers[row](selectedRow);
              adjustLayout(selectedRow);
            });
          /*
          fastTransition(rows.exit())
            .attr('opacity', 0)
            .on('end', remove);
          */
          });
        }
      }),
      exit => exit.call(exit => {
        exit.call(exit => {
          if (!exit.empty()) {
            try {
              exit
              .classed('exiting', true)
              .transition(slowTransition)
              .attr('transform', `translate(${translateX},0)`)
              .on('end', remove);
            } catch (error) {
              // TODO: Find out where the transition goes...
              // console.error('Caught', { error, slowTransition, exit });
            }
          }
        });
      })
    );

  const doc = select(document);
  const hairlines = doc.selectAll('line.hairline.cursor');
  doc.selectAll('svg .canvas')
    .on('mouseenter.harline', () => {
      const infoRowPresent = !select(document).select('g.infoRows').empty();
      hairlines.style('visibility', infoRowPresent ? 'visible' : 'hidden');
    })
    .on('mousemove.harline', (event) => {
      const background = select(event.target.ownerSVGElement).select('.canvasBackground').node();
      const x = pointer(event, background)[0];
      hairlines.attr('x1', x);
      hairlines.attr('x2', x);
    })
    .on('mouseleave.harline', () => {
      hairlines.style('visibility', 'hidden');
    });
}

const DayInfoGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default DayInfoGraph;